// ProducerExplorePage — dedicated public page for a single producer/domaine.
// Renders three layers of content so the fiche never looks empty even when
// the async Atlas Blocks pipeline hasn't landed yet:
//   1. Editorial hero — description + philosophy prose
//   2. Key-facts card — founding year, surface, appellation, certifications,
//      signature cuvées, website (all pulled from `/api/public/producers`)
//   3. Plain-HTML relations strip for LLM crawlers (Phase 2 GEO)
//   4. AtlasBlocksSection — the full generated fact-sheet (30-60 blocks)
//
// Route: /producteur/:slug
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, Building2, MapPin, Calendar, Ruler, Leaf,
  Wine, Globe, Landmark,
} from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import AtlasBlocksSection from "@/components/AtlasBlocksSection";
import ReadingProgressBar from "@/components/ReadingProgressBar";
import { useLang } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const COPY = {
  fr: {
    home: "Accueil", explorer: "Explorer",
    seo_short: (name, sec) => `Le domaine ${name}${sec ? ` — ${sec}` : ""}. Histoire, cuvées et terroirs.`,
    back_all: "Tous les producteurs",
    label_producer: "Producteur",
    open_in_app: "Ouvrir dans l'app",
    website: "Site officiel",
    back_index: "Tous les producteurs",
    identity_aria: "Fiche d'identité",
    identity_h: "Fiche d'identité",
    founded: "Fondé en",
    surface: "Surface",
    appellation: "Appellation",
    location: "Localisation",
    certifications: "Certifications",
    signature_wines: "Cuvées signature",
    address: "Adresse",
    philosophy: "Philosophie",
    is_estate: "est un domaine viticole",
    of_appellation: "de l'appellation",
    located_in: "situé en",
    go_further: "Aller plus loin",
    discover: (name) => `Découvrir ${name}`,
    maps_of: (name) => `Cartes ${name}`,
    ask_ai: "Poser une question à geoVINUM AI",
  },
  en: {
    home: "Home", explorer: "Explorer",
    seo_short: (name, sec) => `The ${name} estate${sec ? ` — ${sec}` : ""}. History, cuvées and terroirs.`,
    back_all: "All producers",
    label_producer: "Producer",
    open_in_app: "Open in the app",
    website: "Official website",
    back_index: "All producers",
    identity_aria: "Estate profile",
    identity_h: "Estate profile",
    founded: "Founded in",
    surface: "Surface",
    appellation: "Appellation",
    location: "Location",
    certifications: "Certifications",
    signature_wines: "Signature cuvées",
    address: "Address",
    philosophy: "Philosophy",
    is_estate: "is a wine estate",
    of_appellation: "of the appellation",
    located_in: "located in",
    go_further: "Go further",
    discover: (name) => `Discover ${name}`,
    maps_of: (name) => `${name} maps`,
    ask_ai: "Ask geoVINUM AI",
  },
  es: {
    home: "Inicio", explorer: "Explorer",
    seo_short: (name, sec) => `La bodega ${name}${sec ? ` — ${sec}` : ""}. Historia, cuvées y terruños.`,
    back_all: "Todos los productores",
    label_producer: "Productor",
    open_in_app: "Abrir en la app",
    website: "Sitio oficial",
    back_index: "Todos los productores",
    identity_aria: "Ficha de identidad",
    identity_h: "Ficha de identidad",
    founded: "Fundada en",
    surface: "Superficie",
    appellation: "Denominación",
    location: "Ubicación",
    certifications: "Certificaciones",
    signature_wines: "Cuvées emblemáticas",
    address: "Dirección",
    philosophy: "Filosofía",
    is_estate: "es una bodega vitivinícola",
    of_appellation: "de la denominación",
    located_in: "situada en",
    go_further: "Ir más lejos",
    discover: (name) => `Descubrir ${name}`,
    maps_of: (name) => `Mapas ${name}`,
    ask_ai: "Preguntar a geoVINUM AI",
  },
  pt: {
    home: "Início", explorer: "Explorer",
    seo_short: (name, sec) => `A quinta ${name}${sec ? ` — ${sec}` : ""}. História, cuvées e terroirs.`,
    back_all: "Todos os produtores",
    label_producer: "Produtor",
    open_in_app: "Abrir na app",
    website: "Site oficial",
    back_index: "Todos os produtores",
    identity_aria: "Ficha de identidade",
    identity_h: "Ficha de identidade",
    founded: "Fundada em",
    surface: "Superfície",
    appellation: "Denominação",
    location: "Localização",
    certifications: "Certificações",
    signature_wines: "Cuvées emblemáticas",
    address: "Morada",
    philosophy: "Filosofia",
    is_estate: "é uma quinta vitícola",
    of_appellation: "da denominação",
    located_in: "situada em",
    go_further: "Ir mais longe",
    discover: (name) => `Descobrir ${name}`,
    maps_of: (name) => `Mapas ${name}`,
    ask_ai: "Perguntar à geoVINUM AI",
  },
};

const ProducerExplorePage = () => {
  const { slug } = useParams();
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiFetch("/api/public/producers?limit=2000");
      if (!cancelled && ok) {
        const item = (data?.items || []).find((it) => it.slug === slug);
        setMeta(item || { slug, name: slug, region: "", country: "", appellation: "" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const displayName = useMemo(() => meta?.name || slug, [meta, slug]);
  const secondary = meta ? [meta.appellation, meta.region, meta.country].filter(Boolean).join(" · ") : "";
  const appelSlug = meta?.appellation ? String(meta.appellation).toLowerCase().replace(/\s+/g, "-") : "";

  return (
    <Layout testId={`producteur-${slug}`}>
      <SEO
        title={`${displayName} — Producteur · geoVINUM`}
        description={
          meta?.description?.slice(0, 155)
          || (meta ? c.seo_short(displayName, secondary) : "")
        }
      />
      <ReadingProgressBar targetSelector={`[data-testid="producteur-${slug}"] article`} />

      <section className="bg-[color:var(--gv-mist)] pt-10 pb-8">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Breadcrumb
            items={[
              { to: "/", label: c.home },
              { to: "/blog", label: c.explorer },
              { label: displayName },
            ]}
          />
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)] mt-4 mb-6" data-testid="producer-back">
            <ArrowLeft size={14} /> {c.back_all}
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3 inline-flex items-center gap-2">
            <Building2 size={11} /> {c.label_producer}{secondary && <> · {secondary}</>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mb-5" data-testid="producer-title">
            {displayName}
          </h1>

          {/* Editorial teaser — description prose from upstream. Falls back
              to a short lede when missing. */}
          {meta?.description && (
            <p
              className="text-[15.5px] leading-relaxed text-[color:var(--gv-ink)]/85 max-w-3xl mb-6"
              data-testid="producer-description"
            >
              {meta.description}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <a
              href={`https://app.geovinum.io/producers/${slug}`}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              data-testid="producer-open-app"
            >
              {c.open_in_app} <ExternalLink size={14} />
            </a>
            {meta?.website && (
              <a
                href={meta.website}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary"
                data-testid="producer-website"
              >
                <Globe size={14} /> {c.website}
              </a>
            )}
            <Link to="/blog" className="btn-secondary" data-testid="producer-back-index">
              <Building2 size={14} /> {c.back_index}
            </Link>
          </div>
        </div>
      </section>

      <article className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          {/* Key-facts card — ensures every producer fiche exposes the core
              data (founding, surface, appellation, certifs, cuvées, address)
              even before Atlas blocks generate. */}
          {meta && (meta.founded_year || meta.surface_ha || meta.appellation || meta.address
              || (Array.isArray(meta.certifications) && meta.certifications.length > 0)
              || (Array.isArray(meta.signature_wines) && meta.signature_wines.length > 0)) && (
            <aside
              className="mb-10 rounded-[6px] border border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/50 p-6"
              aria-label={c.identity_aria}
              data-testid="producer-key-facts"
            >
              <h2 className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-4 inline-flex items-center gap-2">
                <Landmark size={12} /> {c.identity_h}
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[13.5px]">
                {meta.founded_year && (
                  <div className="flex items-start gap-2">
                    <Calendar size={14} className="mt-[3px] text-[color:var(--gv-ink)]/50 shrink-0" />
                    <div>
                      <dt className="text-[color:var(--gv-ink)]/55">{c.founded}</dt>
                      <dd className="text-[color:var(--gv-ink)] font-medium">{meta.founded_year}</dd>
                    </div>
                  </div>
                )}
                {meta.surface_ha && (
                  <div className="flex items-start gap-2">
                    <Ruler size={14} className="mt-[3px] text-[color:var(--gv-ink)]/50 shrink-0" />
                    <div>
                      <dt className="text-[color:var(--gv-ink)]/55">{c.surface}</dt>
                      <dd className="text-[color:var(--gv-ink)] font-medium">{meta.surface_ha} ha</dd>
                    </div>
                  </div>
                )}
                {meta.appellation && (
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="mt-[3px] text-[color:var(--gv-ink)]/50 shrink-0" />
                    <div>
                      <dt className="text-[color:var(--gv-ink)]/55">{c.appellation}</dt>
                      <dd className="text-[color:var(--gv-ink)] font-medium">
                        <Link to={`/explore/${encodeURIComponent(appelSlug)}`} className="hover:text-[color:var(--gv-pink)]">
                          {meta.appellation}
                        </Link>
                      </dd>
                    </div>
                  </div>
                )}
                {(meta.region || meta.country) && (
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="mt-[3px] text-[color:var(--gv-ink)]/50 shrink-0" />
                    <div>
                      <dt className="text-[color:var(--gv-ink)]/55">{c.location}</dt>
                      <dd className="text-[color:var(--gv-ink)] font-medium">
                        {[meta.region, meta.country].filter(Boolean).join(", ")}
                      </dd>
                    </div>
                  </div>
                )}
                {Array.isArray(meta.certifications) && meta.certifications.length > 0 && (
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <Leaf size={14} className="mt-[3px] text-[color:var(--gv-ink)]/50 shrink-0" />
                    <div>
                      <dt className="text-[color:var(--gv-ink)]/55">{c.certifications}</dt>
                      <dd className="text-[color:var(--gv-ink)] font-medium">
                        {meta.certifications.join(" · ")}
                      </dd>
                    </div>
                  </div>
                )}
                {Array.isArray(meta.signature_wines) && meta.signature_wines.length > 0 && (
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <Wine size={14} className="mt-[3px] text-[color:var(--gv-ink)]/50 shrink-0" />
                    <div>
                      <dt className="text-[color:var(--gv-ink)]/55">{c.signature_wines}</dt>
                      <dd className="text-[color:var(--gv-ink)] font-medium">
                        {meta.signature_wines.slice(0, 6).map((w, i) => (
                          <span key={`${w}-${i}`}>{i > 0 ? " · " : ""}<em className="not-italic">{w}</em></span>
                        ))}
                      </dd>
                    </div>
                  </div>
                )}
                {meta.address && (
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <MapPin size={14} className="mt-[3px] text-[color:var(--gv-ink)]/50 shrink-0" />
                    <div>
                      <dt className="text-[color:var(--gv-ink)]/55">{c.address}</dt>
                      <dd className="text-[color:var(--gv-ink)] font-medium">{meta.address}</dd>
                    </div>
                  </div>
                )}
              </dl>
            </aside>
          )}

          {/* Philosophy prose — full-length paragraph when upstream provides it. */}
          {meta?.philosophy && (
            <div className="mb-10 pb-10 border-b border-[color:var(--gv-line)]">
              <h2 className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">{c.philosophy}</h2>
              <p className="text-[15px] leading-relaxed text-[color:var(--gv-ink)]/85" data-testid="producer-philosophy">
                {meta.philosophy}
              </p>
            </div>
          )}

          {/* Phase 2 SEO/GEO — Plain-HTML relations strip so LLM crawlers
              read the producer graph (location chain + signature wines)
              without touching JS. */}
          {meta && (
            <nav
              aria-label="Relations"
              className="mb-10 pb-6 border-b border-[color:var(--gv-line)] text-[13.5px] text-[color:var(--gv-ink)]/85 leading-relaxed"
              data-testid="producer-relations"
            >
              <p>
                <strong>{displayName}</strong> {c.is_estate}
                {meta.appellation ? <> {c.of_appellation} <Link to={`/explore/${encodeURIComponent(appelSlug)}`} className="text-[color:var(--gv-pink)] hover:underline">{meta.appellation}</Link></> : null}
                {meta.region ? <>{meta.appellation ? "," : ""} {c.located_in} <Link to={`/blog?q=${encodeURIComponent(meta.region)}`} className="text-[color:var(--gv-pink)] hover:underline">{meta.region}</Link></> : null}
                {meta.country ? <>, <Link to={`/blog?q=${encodeURIComponent(meta.country)}`} className="text-[color:var(--gv-pink)] hover:underline">{meta.country}</Link></> : null}
                .
              </p>
              <p className="mt-2 text-[color:var(--gv-ink)]/65">
                {c.go_further} —{" "}
                {meta.appellation ? (
                  <>
                    <Link to={`/explore/${encodeURIComponent(appelSlug)}`} className="text-[color:var(--gv-pink)] hover:underline">{c.discover(meta.appellation)}</Link>
                    {" · "}
                  </>
                ) : null}
                <Link to={`/shop?q=${encodeURIComponent(meta.region || displayName)}`} className="text-[color:var(--gv-pink)] hover:underline">{c.maps_of(meta.region || displayName)}</Link>
                {" · "}
                <Link to={`/app#ai`} className="text-[color:var(--gv-pink)] hover:underline">{c.ask_ai}</Link>
              </p>
            </nav>
          )}

          <AtlasBlocksSection producer={displayName} lang={lang} />
        </div>
      </article>
    </Layout>
  );
};

export default ProducerExplorePage;
